import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const ChangePassword = () => {
  const { changePassword } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const t = (en, fr) => language === 'fr' ? fr : en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPw.length < 8) {
      setError(t('Password must be at least 8 characters', 'Le mot de passe doit contenir au moins 8 caracteres'));
      return;
    }
    if (newPw !== confirmPw) {
      setError(t('Passwords do not match', 'Les mots de passe ne correspondent pas'));
      return;
    }

    setLoading(true);
    try {
      await changePassword(currentPw, newPw);
      navigate('/marcom');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="change-password-page" className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-wine/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-wine" strokeWidth={1.5} />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-white mb-2">
              {t('Change Your Password', 'Changez Votre Mot de Passe')}
            </h1>
            <p className="text-white/50 text-sm">
              {t('You must set a new password before continuing.', 'Vous devez definir un nouveau mot de passe avant de continuer.')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-white/70 text-sm font-medium block mb-1.5">
                {t('Current Password', 'Mot de Passe Actuel')}
              </label>
              <Input
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                data-testid="current-password-input"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-lg"
                placeholder={t('Enter current password', 'Entrez le mot de passe actuel')}
              />
            </div>

            <div>
              <label className="text-white/70 text-sm font-medium block mb-1.5">
                {t('New Password', 'Nouveau Mot de Passe')}
              </label>
              <div className="relative">
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  data-testid="new-password-input"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-lg pr-10"
                  placeholder={t('Minimum 8 characters', 'Minimum 8 caracteres')}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-white/70 text-sm font-medium block mb-1.5">
                {t('Confirm Password', 'Confirmer le Mot de Passe')}
              </label>
              <Input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                data-testid="confirm-password-input"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-lg"
                placeholder={t('Confirm new password', 'Confirmer le nouveau mot de passe')}
              />
            </div>

            {error && (
              <p data-testid="change-password-error" className="text-red-400 text-sm">{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="change-password-btn"
              className="w-full bg-wine hover:bg-wine-light text-white rounded-lg h-12 text-base font-medium"
            >
              {loading
                ? t('Updating...', 'Mise a jour...')
                : t('Set New Password', 'Definir le Nouveau Mot de Passe')
              }
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
